import type { FC, ReactNode } from 'react'

import { ArrowUpRight, Clock, Play, Timer } from 'lucide-react'
import { SectionHeading } from '@/components/generals/section-heading'
import { VideoDialog } from '@/components/landing/video-dialog'

import {
    PILLARS,
    ROOSTER_SLOTS,
    SETUP_STEPS,
    VIDEO_POSTER,
    external
} from '@/constants/leesritme'

const WEEK_DAYS = ['Ma', 'Di', 'Wo', 'Do', 'Vr']

const PillarVisual: FC<{ index: number }> = ({ index }): ReactNode => {
    if (index === 0) {
        return (
            <div className='flex gap-1.5' aria-hidden='true'>
                {WEEK_DAYS.map((day, dayIndex) => (
                    <span
                        key={day}
                        className={dayIndex === 0
                            ? 'grid h-12 flex-1 place-items-center rounded-xl border-2 border-coral bg-coral-soft text-sm font-bold text-coral-deep'
                            : 'grid h-12 flex-1 place-items-center rounded-xl border border-ink/10 bg-white text-sm font-bold text-ink/70'}
                    >
                        {day}
                    </span>
                ))}
            </div>
        )
    }

    if (index === 1) {
        return (
            <div className='grid grid-cols-2 gap-2' aria-hidden='true'>
                <span className='rounded-xl bg-white px-3 py-2.5 text-sm'>
                    <span className='block font-bold text-ink'>In de klas</span>
                    <span className='block text-muted-foreground'>Zo’n 12 minuten</span>
                </span>

                <span className='rounded-xl bg-white px-3 py-2.5 text-sm'>
                    <span className='block font-bold text-ink'>Thuis</span>
                    <span className='block text-muted-foreground'>Zo’n 10 minuten</span>
                </span>
            </div>
        )
    }

    return null
}

export const SolutionSection: FC = (): ReactNode => {
    return (
        <section id='oplossing' className='relative bg-card py-20 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    eyebrow='De oplossing'
                    title={(
                        <>
                            Meer lezen. Elke dag. <span className='font-medium text-leaf italic'>Zonder extra werk.</span>
                        </>
                    )}
                    intro='Leesritme zet elke dag een rijke tekst klaar, kijkt de vragen na en stuurt een tweede tekst mee naar huis. Je leesmethode blijft gewoon staan: Leesritme vult hem aan.'
                />

                <div className='reveal mt-12 rounded-[1.75rem] border border-ink/10 bg-paper p-6 sm:p-8'>
                    <div className='flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-10'>
                        <div className='shrink-0'>
                            <p className='inline-flex items-center gap-2 rounded-full bg-leaf px-3 py-1 text-sm font-bold text-white'>
                                <Timer className='size-4' aria-hidden='true' />
                                Opgezet in vijf minuten
                            </p>

                            <p className='mt-3 max-w-[16rem] font-heading text-xl leading-snug font-semibold text-ink'>
                                Zo staat je klas klaar
                            </p>
                        </div>

                        <ol className='grid flex-1 gap-4 sm:grid-cols-3'>
                            {SETUP_STEPS.map((step, index) => {
                                const Icon = step.icon

                                return (
                                    <li key={step.title} className='relative flex items-start gap-3 rounded-2xl bg-white p-4 shadow-soft'>
                                        <span className='grid size-10 shrink-0 place-items-center rounded-xl bg-sun-soft text-ink'>
                                            <Icon className='size-5' aria-hidden='true' />
                                        </span>

                                        <span>
                                            <span className='block text-xs font-bold tracking-[0.12em] text-muted-foreground uppercase'>
                                                Stap {index + 1}
                                            </span>

                                            <span className='mt-0.5 block font-bold text-ink'>
                                                {step.title}
                                            </span>

                                            <span className='block text-sm text-muted-foreground'>
                                                {step.text}
                                            </span>
                                        </span>
                                    </li>
                                )
                            })}
                        </ol>
                    </div>
                </div>

                <ul className='mt-6 grid gap-6 md:grid-cols-2'>
                    {PILLARS.map((pillar, index) => {
                        const Icon = pillar.icon

                        return (
                            <li key={pillar.title} className='reveal flex flex-col rounded-[1.75rem] border border-ink/10 bg-paper p-7 sm:p-8'>
                                <span className='grid size-12 place-items-center rounded-2xl bg-leaf text-white'>
                                    <Icon className='size-6' aria-hidden='true' />
                                </span>

                                <h3 className='mt-6 font-heading text-2xl leading-tight font-semibold text-ink'>
                                    {pillar.title}
                                </h3>

                                <p className='mt-3 text-[1.0625rem] leading-relaxed text-muted-foreground'>
                                    {pillar.text}
                                </p>

                                <div className='mt-auto pt-6'>
                                    <PillarVisual index={index} />

                                    <p className='mt-4 border-l-[3px] border-sun pl-4 font-semibold text-ink'>
                                        {pillar.note}
                                    </p>
                                </div>
                            </li>
                        )
                    })}
                </ul>

                <div className='mt-24 grid gap-10 lg:grid-cols-[1fr_1.6fr] lg:gap-14'>
                    <div>
                        <p className='inline-flex items-center gap-2 text-sm font-bold tracking-[0.14em] text-leaf uppercase'>
                            <Clock className='size-4' aria-hidden='true' />
                            Tien minuten per dag
                        </p>

                        <h3 className='mt-4 font-heading font-soft text-3xl leading-tight font-semibold text-ink sm:text-4xl'>
                            Geen ruimte in je rooster? <span className='font-medium text-leaf italic'>Het past in tijd die je al hebt.</span>
                        </h3>

                        <p className='mt-5 text-lg leading-relaxed text-muted-foreground'>
                            Leesritme vraagt geen nieuw vak. Je zet het in op een moment dat al in je week zit.
                        </p>

                        <p className='mt-5 text-sm text-muted-foreground'>
                            Onderbouwing:{' '}
                            <a href={external('/verantwoording')} className='font-semibold text-ink underline decoration-ink/20 underline-offset-4 hover:decoration-ink'>
                                Bus e.a., 2024; Merke e.a., 2024
                            </a>
                        </p>
                    </div>

                    <ol className='grid gap-3 sm:grid-cols-2'>
                        {ROOSTER_SLOTS.map((slot, index) => {
                            const Icon = slot.icon

                            return (
                                <li
                                    key={slot.title}
                                    className={index === 0
                                        ? 'reveal flex gap-4 rounded-2xl border border-ink/10 bg-paper p-5 sm:col-span-2'
                                        : 'reveal flex gap-4 rounded-2xl border border-ink/10 bg-paper p-5'}
                                >
                                    <span className='grid size-11 shrink-0 place-items-center rounded-xl bg-white text-leaf shadow-soft'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <span>
                                        <span className='flex flex-wrap items-center gap-2'>
                                            <span className='font-heading text-lg font-semibold text-ink'>
                                                {slot.title}
                                            </span>
                                        </span>

                                        <span className='mt-1 block text-[0.9375rem] leading-relaxed text-muted-foreground'>
                                            {slot.text}
                                        </span>
                                    </span>
                                </li>
                            )
                        })}
                    </ol>
                </div>

                <div className='reveal mt-24 overflow-hidden rounded-[2rem] bg-ink text-paper'>
                    <div className='grid items-center lg:grid-cols-[1fr_1.25fr]'>
                        <div className='p-8 sm:p-12'>
                            <p className='text-sm font-bold tracking-[0.14em] text-sun uppercase'>
                                Video
                            </p>

                            <h3 className='mt-4 font-heading font-soft text-3xl leading-tight font-semibold sm:text-4xl'>
                                Leesritme in vier minuten
                            </h3>

                            <p className='mt-4 text-lg leading-relaxed text-[#c3cad8]'>
                                Een week met Iris: in de klas en thuis, de stelling, de Leesshow en hoe je het inzet naast je leesmethode.
                            </p>

                            <div className='mt-8 flex flex-wrap items-center gap-4'>
                                <VideoDialog
                                    label='Bekijk de video: Leesritme in vier minuten'
                                    className='inline-flex min-h-13 items-center gap-2.5 rounded-full bg-paper px-6 font-semibold text-ink transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sun focus-visible:ring-offset-2 focus-visible:ring-offset-ink'
                                >
                                    <Play className='size-4 fill-current' aria-hidden='true' />
                                    Bekijk de video
                                </VideoDialog>

                                <a
                                    href={external('/themaweken')}
                                    className='inline-flex items-center gap-1.5 font-semibold text-paper/90 underline decoration-white/30 underline-offset-4 hover:decoration-white'
                                >
                                    Bekijk de reeksen per onderwerp
                                    <ArrowUpRight className='size-4' aria-hidden='true' />
                                </a>
                            </div>
                        </div>

                        <VideoDialog
                            label='Speel de video af: Leesritme in vier minuten'
                            className='group relative block aspect-video w-full overflow-hidden text-left focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sun focus-visible:ring-inset lg:h-full lg:aspect-auto'
                        >
                            <img
                                src={VIDEO_POSTER}
                                alt=''
                                loading='lazy'
                                decoding='async'
                                className='absolute inset-0 size-full object-cover transition duration-500 group-hover:scale-[1.02]'
                            />

                            <span className='absolute inset-0 bg-linear-to-r from-ink/30 to-transparent' aria-hidden='true' />

                            <span className='absolute top-1/2 left-1/2 grid size-20 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-coral text-white shadow-lift transition group-hover:scale-110'>
                                <Play className='ml-1 size-8 fill-current' aria-hidden='true' />
                            </span>
                        </VideoDialog>
                    </div>
                </div>
            </div>
        </section>
    )
}
